import React, {useRef, useEffect, useMemo} from 'react';
import {Props} from './ac-checkbox.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {TYPES} from '@asdf-constants';

const _CLASSES = {
  MAIN: 'ac-checkbox',
  DISABLED: 'ac-checkbox--disabled',
  POSITION: {
    BEFORE: 'ac-checkbox--before',
    AFTER: 'ac-checkbox--after',
  },
  TYPES: {
    CHECKBOX: 'ac-checkbox--checkbox',
    RADIO: 'ac-checkbox--radiobutton',
  },
  INPUT: {
    MAIN: 'ac-checkbox__input',
    DISABLED: 'ac-checkbox__input--disabled',
  },
  DISPLAY: {
    MAIN: 'ac-checkbox__display',
    DISABLED: 'ac-checkbox__display--disabled',
  },
  LABEL: {
    MAIN: 'ac-checkbox__label',
    DISABLED: 'ac-checkbox__label--disabled',
    POSITION: {
      BEFORE: 'ac-checkbox__label--before',
      AFTER: 'ac-checkbox__label--after',
    },
  },
};

// Component
const AcCheckbox = ({
  children = 'Checkbox label',
  name,
  id,
  value,
  position = 'after',
  checked = false,
  required = true,
  type = TYPES.CHECKBOX,
  validation,
  callback,
  disabled,
  className,
}) => {
  const $label = useRef(null);
  const $input = useRef(null);

  useEffect(() => {
    if (validation) validation(name, value, required, type, checked);
  }, [checked]);

  const handleLabelClick = event => {
    if (event && event.persist) event.persist();
    if (event && event.stopPropagation) event.stopPropagation();
    if (event && event.preventDefault) event.preventDefault();

    handleChange(event);
  };

  const handleChange = event => {
    if (event && event.persist) event.persist();
    if (event && event.preventDefault) event.preventDefault();

    if (!$input || !$input.current) return;
    const selected = !checked;

    if (callback) callback(event, name, value, type, selected);
    if (validation) validation(name, value, required, type, selected);
  };

  const getDisplayClassNames = useMemo(() => {
    return clsx(_CLASSES.DISPLAY.MAIN, disabled && _CLASSES.DISPLAY.DISABLED);
  }, [disabled]);

  const getInputClassNames = useMemo(() => {
    return clsx(_CLASSES.INPUT.MAIN, disabled && _CLASSES.INPUT.DISABLED);
  }, [disabled]);

  const getLabelClassNames = useMemo(() => {
    return clsx(
      _CLASSES.LABEL.MAIN,
      disabled && _CLASSES.LABEL.DISABLED,
      position && _CLASSES.LABEL.POSITION[position.toUpperCase()]
    );
  }, [disabled, position]);

  const getMainClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      disabled && _CLASSES.DISABLED,
      position && _CLASSES.POSITION[position.toUpperCase()],
      type && _CLASSES.TYPES[type.toUpperCase()],
      className
    );
  }, [disabled, position, type, className]);

  return (
    <label id={`ac-checkbox-label-${id}`} htmlFor={id} className={getMainClassNames} ref={$label}>
      <input
        type={type}
        id={id}
        value={value}
        name={name}
        disabled={disabled}
        checked={checked}
        onChange={handleChange}
        className={getInputClassNames}
        aria-labelledby={`ac-checkbox-label-${id}`}
        ref={$input}
      />
      {position === 'before' && (
        <summary
          className={getLabelClassNames}
          onClick={handleLabelClick}
          onKeyUp={handleLabelClick}
          aria-labelledby={`ac-checkbox-label-${id}`}
        >
          {children}
        </summary>
      )}
      <span
        className={getDisplayClassNames}
        role={'button'}
        aria-labelledby={`ac-checkbox-label-${id}`}
        data-active={checked}
      />
      {position === 'after' && (
        <summary
          className={getLabelClassNames}
          onClick={handleLabelClick}
          onKeyUp={handleLabelClick}
          aria-labelledby={`ac-checkbox-label-${id}`}
        >
          {children}
        </summary>
      )}
    </label>
  );
};

AcCheckbox.displayName = 'AcCheckbox';
AcCheckbox.propTypes = Props;

export {AcCheckbox};
export default AcCheckbox;
